/**
 * StatsTicker — animated social-proof counter for the landing page.
 *
 * Polls `/api/public/stats` once on mount + every 60s while the page is
 * visible. Numbers ease in from 0 to the live value on first paint and
 * re-tween smoothly on subsequent updates so visitors see them grow in
 * real time (e.g. when a new signup pops the counter from 4,237 → 4,238).
 *
 * Designed for the landing page only — admin live stats stay behind auth.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Sparkles, Users, Zap, TrendingUp } from "lucide-react";
import { useAbVariant } from "../hooks/useAbVariant";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 60_000;
const TWEEN_DURATION_MS = 1100;

// ─── A/B copy variants for StatsTicker ───────────────────────────────────
// Three distinct angles to test which message drives the most hero-CTA
// clicks. Same numbers, different framing.
const STATS_VARIANTS = ["control", "urgency", "proof"];
const VARIANT_COPY = {
  control: {
    badge: "Live · founders building right now",
    labels: {
      creatives: "Creatives generated",
      users:     "Founders building",
      gen24h:    "Generations · last 24h",
      signups:   "New signups · last 24h",
    },
  },
  urgency: {
    badge: "Today only · join while seats are open",
    labels: {
      creatives: "Posts shipped",
      users:     "Already inside",
      gen24h:    "Made today",
      signups:   "Joined in 24h",
    },
  },
  proof: {
    badge: "Trusted by indie founders worldwide",
    labels: {
      creatives: "Creatives produced",
      users:     "Active stores",
      gen24h:    "AI runs · last 24h",
      signups:   "Signups · last 24h",
    },
  },
};

// ─── Hook: animate a number from its previous value to the current value ──
const useTweenedNumber = (target) => {
  const [display, setDisplay] = useState(0);
  const fromRef = useRef(0);
  const startRef = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (target == null || target === fromRef.current) return undefined;
    const from = fromRef.current;
    const to = target;
    startRef.current = performance.now();
    const step = (now) => {
      const elapsed = now - startRef.current;
      const t = Math.min(1, elapsed / TWEEN_DURATION_MS);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const value = Math.round(from + (to - from) * eased);
      setDisplay(value);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        fromRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target]);

  return display;
};

const formatNumber = (n) => {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString();
};

const StatCell = ({ icon: Icon, value, label, accent = "coral", testid }) => {
  const tweened = useTweenedNumber(value);
  const accents = {
    coral:  "text-coral-500",
    citrus: "text-citrus-500",
    violet: "text-violet-500",
    amber:  "text-amber-500",
  };
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <span className={`inline-flex items-center justify-center w-10 h-10 rounded-xl bg-ink-950/5 ${accents[accent]}`}>
        <Icon className="w-5 h-5" />
      </span>
      <div className="leading-tight">
        <p className="font-display font-black text-2xl text-ink-950 tabular-nums" data-testid={`${testid}-value`}>
          {formatNumber(tweened)}
        </p>
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-500">
          {label}
        </p>
      </div>
    </div>
  );
};

export const StatsTicker = () => {
  const [stats, setStats] = useState(null);
  const [errored, setErrored] = useState(false);
  const { variant } = useAbVariant("stats_ticker_copy", STATS_VARIANTS);
  const copy = VARIANT_COPY[variant] || VARIANT_COPY.control;

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const r = await axios.get(`${API}/public/stats`);
        if (!cancelled) {
          setStats(r.data);
          setErrored(false);
        }
      } catch (_) {
        if (!cancelled) setErrored(true);
      }
    };
    fetchStats();
    const id = setInterval(fetchStats, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Hide the entire band if we couldn't fetch — better no proof than a
  // broken row of dashes. The marquee below us still gives the page energy.
  if (errored) return null;

  return (
    <section
      className="relative border-y border-ink-200 bg-gradient-to-r from-white via-ink-50 to-white py-7 px-6"
      data-testid="landing-stats-ticker"
      data-ab-variant={variant}
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-center gap-2 mb-4">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          <p
            className="text-[10px] font-bold uppercase tracking-[0.22em] text-ink-500"
            data-testid="stats-ticker-badge"
          >
            {copy.badge}
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-12 items-center justify-items-center">
          <StatCell
            icon={Sparkles}
            value={stats?.total_creatives}
            label={copy.labels.creatives}
            accent="coral"
            testid="stat-creatives"
          />
          <StatCell
            icon={Users}
            value={stats?.total_users}
            label={copy.labels.users}
            accent="violet"
            testid="stat-users"
          />
          <StatCell
            icon={Zap}
            value={stats?.generations_24h}
            label={copy.labels.gen24h}
            accent="amber"
            testid="stat-24h"
          />
          <StatCell
            icon={TrendingUp}
            value={stats?.signups_24h}
            label={copy.labels.signups}
            accent="citrus"
            testid="stat-signups"
          />
        </div>
      </div>
    </section>
  );
};

export default StatsTicker;
